import { useMutation, useQueryClient } from "@tanstack/react-query";

import { useApi } from "@/api/hooks/useApi";
import type { DocumentsFileRequest, DocumentsFolderRequest } from "@/api/types";
import { useFlashToast } from "@/components/FlashToast/FlashToast";
import { useProjectId } from "@/hooks/Network/useProjectId";
import { QUERY_KEYS } from "@/query-keys";
import { useTranslation } from "@/translations";
import type { ApiResponseType } from "@/types/api-types";

const useDeleteRootFolder = () => {
  const api = useApi();
  const showFlashToast = useFlashToast();
  const queryClient = useQueryClient();
  const { t } = useTranslation();
  const projectId = useProjectId();

  return useMutation({
    mutationFn: api.deleteDocumentsRootFolderByIdV1,
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: QUERY_KEYS.DOCUMENTS_ROOT_LIST(projectId) });
      showFlashToast({
        type: "success",
        title: t("model.document.root.action.delete.notification.success"),
      });
    },
    onError: () => {
      showFlashToast({
        type: "error",
        title: t("model.document.root.action.delete.notification.failure"),
      });
    },
  });
};

const useDeleteFolder = () => {
  const api = useApi();
  const showFlashToast = useFlashToast();
  const queryClient = useQueryClient();
  const { t } = useTranslation();
  const projectId = useProjectId();

  return useMutation({
    mutationFn: (payload: { parentFolderId: string; folderId: string }) =>
      api.deleteDocumentsFolderByIdV1(payload.folderId),
    onSuccess: (_, payload) => {
      showFlashToast({
        type: "success",
        title: t("model.document.folder.action.delete.notification.success"),
      });

      void queryClient.invalidateQueries({ queryKey: QUERY_KEYS.DOCUMENTS_LIST(projectId, payload.parentFolderId) });
    },
    onError: () => {
      showFlashToast({
        type: "error",
        title: t("model.document.folder.action.delete.notification.failure"),
      });
    },
  });
};

const useDeleteFile = () => {
  const api = useApi();
  const showFlashToast = useFlashToast();
  const queryClient = useQueryClient();
  const { t } = useTranslation();
  const projectId = useProjectId();

  return useMutation({
    mutationFn: (payload: { folderId: string; fileId: string }) => api.deleteDocumentsFileByIdV1(payload.fileId),
    onSuccess: (_, payload) => {
      showFlashToast({
        type: "success",
        title: t("model.document.file.action.delete.notification.success"),
      });
      void queryClient.invalidateQueries({ queryKey: QUERY_KEYS.DOCUMENTS_LIST(projectId, payload.folderId) });
    },

    onError: () => {
      showFlashToast({
        type: "error",
        title: t("model.document.file.action.delete.notification.failure"),
      });
    },
  });
};

const useUpdateFolder = () => {
  const api = useApi();
  const showFlashToast = useFlashToast();
  const queryClient = useQueryClient();
  const { t } = useTranslation();
  const projectId = useProjectId();

  return useMutation({
    mutationFn: ({ id, data }: { id: string; data: DocumentsFolderRequest }) => api.putDocumentsFolderV1(id, data),
    onSuccess() {
      showFlashToast({
        title: t("component.documents.move-item-modal.notification.success"),
        type: "success",
      });
    },
    onError() {
      showFlashToast({
        title: t("component.documents.move-item-modal.notification.error"),
        type: "error",
      });
    },
    onSettled() {
      void queryClient.invalidateQueries({ queryKey: QUERY_KEYS.DOCUMENTS_LIST_ALL(projectId) });
    },
  });
};

const useUpdateFile = () => {
  const api = useApi();
  const showFlashToast = useFlashToast();
  const queryClient = useQueryClient();
  const { t } = useTranslation();
  const projectId = useProjectId();

  return useMutation({
    mutationFn: ({ id, data }: { id: string; data: DocumentsFileRequest }) => api.putDocumentsFileV1(id, data),
    onSuccess() {
      showFlashToast({
        title: t("component.documents.move-item-modal.notification.success"),
        type: "success",
      });
    },
    onError() {
      showFlashToast({
        title: t("component.documents.move-item-modal.notification.error"),
        type: "error",
      });
    },
    onSettled() {
      void queryClient.invalidateQueries({ queryKey: QUERY_KEYS.DOCUMENTS_LIST_ALL(projectId) });
    },
  });
};

const useUpdateFileViewCount = () => {
  const api = useApi();
  const queryClient = useQueryClient();
  const projectId = useProjectId();

  return useMutation({
    mutationFn: (payload: { folderId: string; fileId: string; page?: number }) =>
      api.putDocumentsUpdateViewCountV1(payload.fileId),
    onMutate: (payload) => {
      const pageSize = 10;

      queryClient.setQueryData<ApiResponseType<"getDocumentsDetailsV1">>(
        QUERY_KEYS.DOCUMENTS_LIST(projectId, payload.folderId, {
          Offset: pageSize * (payload.page || 0),
          Limit: pageSize,
        }),
        (currData) => {
          if (!currData) return currData;

          const currFiles = currData.data.items;
          const updatedFiles = currFiles.map((file) => {
            if (file.id === payload.fileId) {
              return { ...file, viewCount: (file.viewCount || 0) + 1 };
            }

            return file;
          });

          return {
            ...currData,
            data: {
              ...currData.data,
              items: updatedFiles,
            },
          };
        },
      );
    },
    onSettled: (_, __, payload) => {
      void queryClient.invalidateQueries({ queryKey: QUERY_KEYS.DOCUMENTS_LIST(projectId, payload.folderId) });
    },
  });
};

export const documentMutations = {
  useDeleteFile,
  useUpdateFile,
  useUpdateFileViewCount,
  useDeleteRootFolder,
  useDeleteFolder,
  useUpdateFolder,
};
